import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Eyebrow, Reveal, GoldButton, OutlineButton, EASE } from "@/components/site/motion";
import { IMAGES } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

const questions = [
  "Who is the ideal audience for our brand?",
  "What brands, interests and cultural influences define that audience?",
  "Which audiences are most relevant to a new product?",
  "Where should we launch or expand?",
  "Which brands have the strongest audience overlap?",
  "What partnerships and sponsorships make strategic sense?",
  "Which consumer segments represent untapped opportunities?",
  "Where should we allocate our marketing investment?",
  "Which locations and environments are most relevant to our target audience?",
  "What should we do next?",
];

const layers = [
  {
    title: "Audience Intelligence",
    text: "Discover consumer interests, affinities, cultural connections and audience opportunities.",
  },
  {
    title: "Cultural Intelligence",
    text: "Understand the artists, brands, entertainment, destinations and experiences influencing different consumer communities.",
  },
  {
    title: "Location Intelligence",
    text: "Understand where audiences and commercial opportunities are concentrated.",
  },
  {
    title: "Retail Intelligence",
    text: "Identify consumer demand, category opportunities and potential areas for expansion.",
  },
  {
    title: "Media Intelligence",
    text: "Connect audience insights with physical and digital media environments to improve targeting and campaign planning.",
  },
  {
    title: "Attention Intelligence",
    text: "Understand how consumers interact with physical environments and media using real-world audience measurement.",
  },
  {
    title: "Predictive Intelligence",
    text: "Turn multiple data signals into recommendations about audiences, locations, partnerships, campaigns and future opportunities.",
  },
];

const askExamples = [
  "We are launching a premium coffee brand in South Africa. Which audiences should we target?",
  "What brands have the strongest affinity with Amapiano audiences?",
  "Which consumer segments should a premium automotive brand target?",
  "What type of retailers would appeal to young professionals?",
  "Which brands would make the strongest strategic partnership for our audience?",
];

const shots = [
  { title: "Desktop dashboard", image: "/assets/intel-mockup-desktop.png" },
  { title: "Mobile companion", image: "/assets/intel-mockup-mobile.png" },
];

export default function HapoIntel() {
  useSEO(
    "Hapo Intelligence — Consumer & Market Intelligence by Hapo Cloud",
    "Hapo Intelligence is an African consumer and market intelligence platform that transforms data into actionable business decisions."
  );

  return (
    <>
      <PageHero
        eyebrow="A Hapo Group Company"
        lines={["Know your audience.", "Understand your market."]}
        description="Make better decisions. Hapo Intelligence is an African consumer and market intelligence platform that transforms data into actionable business decisions."
        image={IMAGES.network}
        logoOnDark="/assets/logos/intel.webp"
        logoOnLight="/assets/logos/intel-dark.webp"
      >
        <GoldButton to="/contact?topic=intel-demo" testId="intel-book-demo">
          Book a Demo
        </GoldButton>
      </PageHero>

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Overview">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Overview</Eyebrow>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="text-xl font-medium leading-relaxed tracking-tight text-ink md:text-2xl lg:text-3xl">
              Built for the realities of African markets, Hapo Intelligence brings together audience behaviour,
              cultural affinities, brands, places, media, location and real-world consumer signals to help businesses
              understand who their customers are, what influences them, where opportunities exist and what they
              should do next.
            </p>
            <p className="mt-8 max-w-2xl text-sm leading-loose text-smoke md:text-base">
              Instead of relying only on traditional demographics, surveys and historical reports, Hapo Intelligence
              uses AI-powered intelligence to uncover the relationships between consumers and the brands, artists,
              entertainment, destinations, places and experiences that shape their preferences.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="From data to decisions">
        <Eyebrow>From Data to Decisions</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          The questions Hapo Intelligence answers.
        </h2>
        <div className="mt-16 grid gap-x-16 md:grid-cols-2">
          {questions.map((q, i) => (
            <Reveal key={q} delay={(i % 2) * 0.06} className="border-t border-ink/10">
              <p className="flex items-baseline gap-4 py-5 text-base font-medium leading-relaxed text-ink md:text-lg">
                <span className="text-intel-gradient text-xl font-extrabold" aria-hidden="true">
                  ?
                </span>
                {q}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Intelligence layers">
        <Eyebrow>Intelligence Built for Business</Eyebrow>
        <p className="mt-6 max-w-3xl text-sm leading-loose text-smoke md:text-base">
          Hapo Intelligence is designed for brands, retailers, property groups, fuel and convenience networks,
          financial institutions, media owners, agencies, universities and other organisations that need a deeper
          understanding of African consumers and markets. The platform combines multiple intelligence layers:
        </p>
        <div className="mt-12">
          {layers.map((l, i) => (
            <Reveal
              key={l.title}
              delay={i * 0.04}
              className="grid gap-4 border-t border-ink/10 py-10 md:grid-cols-12 md:items-baseline"
            >
              <span className="text-intel-gradient text-4xl font-extrabold tracking-tighter md:col-span-2 lg:text-5xl">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="text-xl font-bold tracking-tight text-ink md:col-span-4 lg:text-2xl">{l.title}</h3>
              <p className="text-sm leading-loose text-smoke md:col-span-6 md:text-base">{l.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#080A12] px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Ask Hapo">
        <Eyebrow>Ask Hapo</Eyebrow>
        <h2 className="mt-5 max-w-3xl text-4xl font-extrabold tracking-tighter text-white sm:text-5xl">
          Complex data, answered in plain language.
        </h2>
        <p className="mt-6 max-w-2xl text-sm leading-loose text-smoke md:text-base">
          Hapo Intelligence makes complex data accessible through a conversational AI interface. Instead of
          navigating hundreds of reports, users can simply ask:
        </p>
        <div className="mt-14 grid gap-4">
          {askExamples.map((q, i) => (
            <Reveal key={q} delay={i * 0.06}>
              <div className="flex items-start gap-4 border border-white/10 bg-white/[0.03] p-6">
                <MessageCircle className="mt-1 h-5 w-5 shrink-0 text-[#19D3E8]" aria-hidden="true" />
                <p className="text-base font-medium leading-relaxed text-white md:text-lg">&ldquo;{q}&rdquo;</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="max-w-2xl text-sm leading-loose text-smoke md:text-base">
            Hapo analyses the available intelligence and converts it into clear, actionable recommendations.
          </p>
        </Reveal>
      </section>

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Inside the product">
        <Eyebrow>Inside the Product</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          Intelligence, beautifully usable.
        </h2>
        <div className="mt-16 grid gap-10 md:grid-cols-2">
          {shots.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="overflow-hidden border border-ink/10 bg-[#F7F8FC]">
                <motion.img
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, ease: EASE }}
                  src={s.image}
                  alt={`${s.title} — hapo Intel product interface`}
                  loading="lazy"
                  className="aspect-[3/2] w-full object-cover"
                />
              </div>
              <p className="mt-4 text-sm font-bold text-ink">{s.title}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-smoke">
                From the official hapo Intel brand guide
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-mist px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Vision">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>The Vision</Eyebrow>
            <h2 className="mt-5 text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
              Building Africa&rsquo;s Consumer Intelligence Layer.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="text-base leading-loose text-smoke md:text-lg">
              Africa generates enormous amounts of consumer, cultural, retail, mobility and location data, but much of
              that intelligence remains fragmented. Hapo Intelligence is building the infrastructure to connect those
              signals.
            </p>
            <p className="mt-6 text-base leading-loose text-smoke md:text-lg">
              Our vision is to create an intelligence layer that enables organisations to understand people, places,
              culture, commerce and attention within one platform — starting in South Africa and Kenya and expanding
              across the continent.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Culture data partner">
        <Reveal className="border-l-2 border-gold bg-mist p-8 lg:p-12">
          <Eyebrow>Culture Data Partner</Eyebrow>
          <img src="/assets/partners/qloo-dark.svg" alt="Qloo logo" className="only-dark-theme mt-6 h-8 w-auto" />
          <img
            src="/assets/partners/qloo-white.svg"
            alt=""
            aria-hidden="true"
            className="only-light-theme mt-6 h-8 w-auto"
          />
          <p className="mt-4 max-w-3xl text-sm leading-loose text-ink md:text-base">
            In partnership with Qloo — the cultural intelligence layer for AI, built on the world's largest catalog of
            people, places, brands and interests linked by trillions of anonymized behavioural signals — Hapo
            Intelligence grounds its audience and cultural intelligence in proven global taste data.
          </p>
        </Reveal>
      </section>

      <section className="bg-[#080A12] px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Book a demo">
        <Reveal className="flex flex-wrap items-center justify-between gap-8">
          <div>
            <h2 className="max-w-xl text-3xl font-extrabold tracking-tighter text-white sm:text-4xl">
              Understand Africa better. Decide with intelligence.
            </h2>
            <p className="mt-4 text-sm leading-loose text-smoke">
              Born inside Hapo Cloud — now a standing Hapo Group company.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <GoldButton to="/contact?topic=intel-demo" testId="intel-book-demo-bottom">
              Book a Demo
            </GoldButton>
            <OutlineButton to="/companies/hapo-cloud" testId="intel-back-to-cloud">
              About Hapo Cloud
            </OutlineButton>
          </div>
        </Reveal>
      </section>
    </>
  );
}
